import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import CarouselEditModal from './CarouselEditModal';
import { buildCarouselPageParams } from './carouselConstants';

const TABLE_COLUMNS = ['圖片（英語）', '創建時間', '順序', '操作'];

export default function CarouselPage() {
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function loadRecords(pageNo = 1) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.carouselPageQuery(buildCarouselPageParams(pageNo));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
  }, []);

  function openCreate() {
    setEditId(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditId(row.id);
    setEditOpen(true);
    setMessage('');
  }

  function handleSaved(isEdit) {
    setMessage(isEdit ? '修改成功' : '新增成功');
    loadRecords(page);
  }

  async function confirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await adminApi.deleteCarousel(deleteTarget.id);
      setMessage('刪除成功');
      setDeleteTarget(null);
      await loadRecords(page);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>輪播圖</h2>
        <div className="section-header__actions">
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            新增
          </button>
        </div>
      </div>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-meta">共 {total} 筆</div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.enImage ? (
                      <img src={resolveAdminMediaUrl(row.enImage)} alt="" className="carousel-list-thumb" />
                    ) : (
                      '未設定'
                    )}
                  </td>
                  <td>{formatDateTime(row.createTime)}</td>
                  <td>{row.sort ?? '—'}</td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="btn btn-success btn-sm" onClick={() => openEdit(row)}>
                        修改
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => setDeleteTarget(row)}
                      >
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(next) => loadRecords(next)} />

      <CarouselEditModal
        open={editOpen}
        carouselId={editId}
        onClose={() => setEditOpen(false)}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除輪播圖"
        message="確定刪除此輪播圖？"
        confirmLabel={deleting ? '刪除中…' : '刪除'}
        confirmDisabled={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
